import {
  Banner,
  Bleed,
  BlockStack,
  Box,
  Button,
  Card,
  Checkbox,
  ChoiceList,
  Collapsible,
  Form,
  FormLayout,
  Grid,
  Icon,
  InlineGrid,
  InlineStack,
  ProgressBar,
  Select,
  Text,
  Link,
} from '@shopify/polaris'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  InfoIcon,
  XIcon,
} from '@shopify/polaris-icons'
import MainLayout from '@Layouts/MainLayout.tsx'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import useCountryList from '@Fetcher/hooks/useCountryList.ts'
import useSetupMonitoringStyle from '@Fetcher/hooks/useSetupMonitoringStyle.ts'
import useShopDetails from '@Fetcher/hooks/useShopDetails.ts'
import { TrackingStyle } from '@Types/Enums'
import { useOnboardingActions } from '@Store/slices/onboarding.ts'
import { useNavigate } from 'react-router'
import {
  HarvesterProvider,
  SetupMonitoringStyleDTO,
} from '@Types/harvesterPreference'

type CountryList = {
  label: string
  value: string
}

function Onboarding(): JSX.Element {
  const OnboardingButton = styled.button<{ active?: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: var(--p-space-300) var(--p-space-100);
    border-radius: var(--p-border-radius-200);
    border: none;
    outline: none;
    background: none;
    cursor: ${(props) => (props.active ? 'default' : 'pointer')};
    pointer-events: ${(props) => (props.active ? 'none' : 'default')};

    &:hover {
      background-color: var(--p-color-bg-surface-hover);
      background: var(--p-color-bg-surface-active);
    }
  `
  const { t: translate } = useTranslation()
  const navigate = useNavigate()

  const {
    setOnboardingCompleted,
    setOnboardingStarted,
    setOnboardingCanceled,
    setFirstRunLogin,
    setMenuItemClicked,
    setOnboardingFormSubmitted,
  } = useOnboardingActions()

  const [shopDetailsFetcher, { data: shopDetailsData }] = useShopDetails()
  const [setupMonitoringStyle] = useSetupMonitoringStyle()
  const [formData, setFormData] = useState<SetupMonitoringStyleDTO>({
    acceptTerms: true,
    companyHarvesterPreference: {
      match_with: 'BARCODE',
      companyHarvesterProviders: [],
    },
    type: TrackingStyle.URL_BASED,
  })
  const [isStepOneOpen, setStepOneOpen] = useState<boolean>(true)
  const [isStepTwoOpen, setStepTwoOpen] = useState<boolean>(false)
  const [isGoogleShoppingSelected, setGoogleShoppingSelected] =
    useState<boolean>(false)
  const [isAmazonSelected, setAmazonSelected] = useState<boolean>(false)
  const [trackingStyle, setTrackingStyle] = useState<
    TrackingStyle | undefined
  >()
  const [completedTask, setCompletedTask] = useState<number>(0)
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false)
  const [isSubmitButtonLoading, setSubmitButtonLoading] = useState(false)
  const [countryFetcher, { data: countryList }] = useCountryList()

  const [googleShoppingCountryList, setGoogleShoppingCountryList] = useState<
    CountryList[]
  >([])
  const [amazonCountryList, setAmazonCountryList] = useState<CountryList[]>([])

  const [selectedGoogleShoppingOption, setSelectedGoogleShoppingOption] =
    useState<string>()
  const [selectedAmazonOption, setSelectedAmazonOption] = useState<string>()

  useEffect(() => {
    const result = []

    for (const provider of ['DATAEDIS', 'PRICE_API']) {
      if (isAmazonSelected && selectedAmazonOption) {
        result.push({
          harvesterProvider: {
            provider: provider,
            channel: 'AMAZON',
            country: selectedAmazonOption,
          } as HarvesterProvider,
        })
      }

      if (isGoogleShoppingSelected && selectedGoogleShoppingOption) {
        result.push({
          harvesterProvider: {
            provider: provider,
            channel: 'GOOGLE_SHOPPING',
            country: selectedGoogleShoppingOption,
          } as HarvesterProvider,
        })
      }
    }

    console.log(result)

    setFormData({
      ...formData,
      companyHarvesterPreference: {
        ...formData.companyHarvesterPreference,
        companyHarvesterProviders: result,
      },
    })
  }, [
    isAmazonSelected,
    isGoogleShoppingSelected,
    selectedAmazonOption,
    selectedGoogleShoppingOption,
  ])

  useEffect(() => {
    if (countryList) {
      setGoogleShoppingCountryList(
        countryList
          .filter((country: Record<string, string | boolean>) => {
            return country.googleShoppingEnabled
          })
          .map((country: Record<string, string | boolean>) => {
            return { value: country.isoCode, label: country.country }
          }),
      )

      setAmazonCountryList(
        countryList
          .filter((country: Record<string, string | boolean>) => {
            return country.amazonShoppingEnabled
          })
          .map((country: Record<string, string | boolean>) => {
            return { value: country.isoCode, label: country.country }
          }),
      )
    }
  }, [countryList])

  useEffect(() => {
    if (shopDetailsData) {
      setFormData({
        ...formData,
        country: shopDetailsData.data.shop.billingAddress.countryCodeV2,
        currency: shopDetailsData.data.shop.currencyCode,
        url: shopDetailsData.data.shop.myshopifyDomain,
      })
    }
  }, [shopDetailsData])

  useEffect(() => {
    setOnboardingStarted(true)
    setOnboardingCanceled(false)
    countryFetcher(true)
    shopDetailsFetcher()
  }, [])

  const renderTrackingStyleChildren = (isSelected: boolean) =>
    isSelected && (
      <Box background={'bg-fill'} padding={'400'} borderRadius={'200'}>
        <BlockStack gap={'200'}>
          <InlineStack
            gap={{ xs: '100', sm: '100', md: '400', lg: '400', xl: '400' }}
            blockAlign={'center'}
          >
            <Checkbox
              label={
                <InlineStack
                  gap={{
                    xs: '100',
                    sm: '100',
                    md: '400',
                    lg: '400',
                    xl: '400',
                  }}
                  blockAlign={'center'}
                  align={'space-between'}
                >
                  <Box minWidth={'110px'}>
                    <Text as={'p'} variant={'bodyMd'}>
                      Google Shopping
                    </Text>
                  </Box>
                  <Select
                    options={googleShoppingCountryList}
                    placeholder={'Select country'}
                    label={'Select country'}
                    labelHidden
                    value={selectedGoogleShoppingOption}
                    onChange={(selected) => {
                      setSelectedGoogleShoppingOption(selected)
                      setGoogleShoppingSelected(true)
                    }}
                  />
                </InlineStack>
              }
              checked={isGoogleShoppingSelected}
              onChange={() =>
                setGoogleShoppingSelected(!isGoogleShoppingSelected)
              }
            />
          </InlineStack>
          <InlineStack
            gap={{ xs: '100', sm: '100', md: '400', lg: '400', xl: '400' }}
            blockAlign={'center'}
          >
            <Checkbox
              label={
                <InlineStack
                  gap={{
                    xs: '100',
                    sm: '100',
                    md: '400',
                    lg: '400',
                    xl: '400',
                  }}
                  blockAlign={'center'}
                  align={'space-between'}
                >
                  <Box minWidth={'110px'}>
                    <Text as={'p'} variant={'bodyMd'}>
                      Amazon
                    </Text>
                  </Box>
                  <Select
                    options={amazonCountryList}
                    placeholder={'Select country'}
                    label={'Select country'}
                    labelHidden
                    value={selectedAmazonOption}
                    onChange={(selected) => {
                      setSelectedAmazonOption(selected)
                      setAmazonSelected(true)
                    }}
                  />
                </InlineStack>
              }
              checked={isAmazonSelected}
              onChange={() => setAmazonSelected(!isAmazonSelected)}
            />
          </InlineStack>
          <Banner tone={'info'} icon={InfoIcon}>
            Prisync automatically matches your products with competitors using
            product barcodes (GTIN, EAN or UPCs).
          </Banner>
        </BlockStack>
      </Box>
    )

  let isSubmitButtonEnabled = false

  if (termsAccepted && trackingStyle) {
    if (
      [TrackingStyle.CHANNEL_BASED, TrackingStyle.HYBRID].includes(
        trackingStyle,
      )
    ) {
      if (isGoogleShoppingSelected && isAmazonSelected) {
        isSubmitButtonEnabled =
          !!selectedGoogleShoppingOption && !!selectedAmazonOption
      } else
        isSubmitButtonEnabled =
          (isGoogleShoppingSelected && !!selectedGoogleShoppingOption) ||
          (isAmazonSelected && !!selectedAmazonOption)
    } else {
      isSubmitButtonEnabled = true
    }
  }

  const handleCancelOnboarding = () => {
    setOnboardingCanceled(true)

    setOnboardingFormSubmitted(false)
    setMenuItemClicked('/')
    setFirstRunLogin(false)
    setOnboardingCompleted(false)
    setOnboardingStarted(false)
    setGoogleShoppingSelected(false)
    setAmazonSelected(false)
    setTermsAccepted(false)
    setTrackingStyle(undefined)
    setStepOneOpen(true)
    setStepTwoOpen(false)
    setFormData({
      acceptTerms: true,
      companyHarvesterPreference: {
        match_with: 'BARCODE',
        companyHarvesterProviders: [],
      },
      type: TrackingStyle.URL_BASED,
    })

    navigate('/')
  }

  return (
    <MainLayout
      title={translate('Prisync.onboarding.label')}
      subtitle={'Use this personalized guide to get your store up and running.'}
      fullWidth={false}
    >
      <Form
        preventDefault={true}
        onSubmit={async () => {
          setSubmitButtonLoading(true)
          await setupMonitoringStyle(formData)
          setOnboardingFormSubmitted(true)
          setOnboardingCompleted(true)
          setOnboardingStarted(true)
        }}
      >
        <FormLayout>
          <Card roundedAbove="sm">
            <Box padding="400">
              <InlineStack align="end" blockAlign="start">
                <Button
                  icon={XIcon}
                  variant="tertiary"
                  onClick={handleCancelOnboarding}
                  accessibilityLabel="Cancel onboarding"
                />
              </InlineStack>
            </Box>

            <BlockStack gap="100">
              <InlineGrid
                columns={{ lg: 12 }}
                alignItems={'center'}
                gap={'400'}
              >
                <Grid.Cell columnSpan={{ lg: 1 }}>
                  <ProgressBar
                    progress={(completedTask / 2) * 100}
                    size={'small'}
                  ></ProgressBar>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ lg: 3 }}>
                  <Text as={'p'} variant={'bodyMd'}>
                    {completedTask} of 2 tasks completed
                  </Text>
                </Grid.Cell>
              </InlineGrid>
              <OnboardingButton
                onClick={() => {
                  setStepOneOpen(!isStepOneOpen)
                  setStepTwoOpen(!isStepTwoOpen)
                  setTrackingStyle(undefined)
                  setCompletedTask(1)
                  setSelectedGoogleShoppingOption('')
                  setGoogleShoppingSelected(false)
                  setSelectedAmazonOption('')
                  setAmazonSelected(false)
                }}
                active={isStepOneOpen}
              >
                <Text as={'span'} variant={'bodyMd'}>
                  1. Terms and Policy
                </Text>
                <Bleed>
                  <Icon
                    source={isStepOneOpen ? ChevronUpIcon : ChevronDownIcon}
                  ></Icon>
                </Bleed>
              </OnboardingButton>
              <Collapsible id={'step-1'} open={isStepOneOpen}>
                <Box
                  background={'bg-surface-secondary'}
                  padding={'200'}
                  borderRadius={'200'}
                  as={'section'}
                  insetBlockStart={'2400'}
                >
                  <InlineStack align={'start'} blockAlign={'center'}>
                    <Checkbox
                      label={
                        <span>
                          To continue using Prisync, you must accept our{' '}
                          <Link
                            url="https://prisync.com/terms-of-service/"
                            target="_blank"
                          >
                            Terms and Service
                          </Link>{' '}
                          and{' '}
                          <Link
                            url="https://prisync.com/privacy-policy/"
                            target="_blank"
                          >
                            Privacy Policy
                          </Link>
                        </span>
                      }
                      checked={termsAccepted}
                      onChange={(newChecked) => {
                        setTermsAccepted(newChecked)
                        setCompletedTask(newChecked ? 1 : 0)
                        setTrackingStyle(undefined)
                        setStepOneOpen(
                          newChecked ? !isStepOneOpen : isStepOneOpen,
                        )
                        setStepTwoOpen(
                          newChecked ? !isStepTwoOpen : isStepTwoOpen,
                        )
                      }}
                    ></Checkbox>
                  </InlineStack>
                </Box>
              </Collapsible>
              <OnboardingButton
                onClick={() => {
                  setStepTwoOpen(!isStepTwoOpen)
                  setStepOneOpen(!isStepOneOpen)
                }}
                active={isStepTwoOpen}
                disabled={!termsAccepted}
              >
                <Text as={'span'} variant={'bodyMd'}>
                  2. Select Your Competitor Tracking Style
                </Text>
                <Bleed>
                  <Icon
                    source={isStepTwoOpen ? ChevronUpIcon : ChevronDownIcon}
                  ></Icon>
                </Bleed>
              </OnboardingButton>
              <Collapsible id={'step-2'} open={isStepTwoOpen}>
                <Box
                  background={'bg-surface-secondary'}
                  padding={'200'}
                  borderRadius={'200'}
                >
                  <ChoiceList
                    title={''}
                    choices={[
                      {
                        label: 'Channel Based',
                        helpText:
                          'Automatically track competitors through sales channels like Google Shopping and Amazon using product barcodes. Ensure your catalog includes barcodes.',
                        value: TrackingStyle.CHANNEL_BASED,
                        renderChildren: renderTrackingStyleChildren,
                      },
                      {
                        label: 'URL Based',
                        helpText:
                          'Manually add competitor URLs individually or in bulk. Ideal for stores with a stable competitor list.',
                        value: TrackingStyle.URL_BASED,
                      },
                      {
                        label: 'Hybrid Panel',
                        helpText:
                          'Combine both methods--track competitors via sales channels and manually add URLs as needed.',
                        value: TrackingStyle.HYBRID,
                        renderChildren: renderTrackingStyleChildren,
                      },
                    ]}
                    selected={[trackingStyle ?? '']}
                    onChange={(value: TrackingStyle[]) => {
                      setFormData({ ...formData, type: value[0] })
                      setTrackingStyle(value[0])
                      setCompletedTask(2)
                    }}
                  />
                </Box>
              </Collapsible>
              <InlineStack align={'end'}>
                <Button
                  variant={'primary'}
                  submit
                  disabled={!isSubmitButtonEnabled}
                  loading={isSubmitButtonLoading}
                >
                  Complete
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </FormLayout>
      </Form>
    </MainLayout>
  )
}

export default Onboarding
